import { useState, useCallback, useEffect } from "react";
import { Bug, X, Send, AlertTriangle, Lightbulb, HelpCircle, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";

const CATEGORIES = [
  { value: "bug", label: "Bug Report", icon: Bug, color: "text-red-500" },
  { value: "feature", label: "Feature Request", icon: Lightbulb, color: "text-amber-500" },
  { value: "improvement", label: "Improvement", icon: AlertTriangle, color: "text-blue-500" },
  { value: "question", label: "Question", icon: HelpCircle, color: "text-green-500" },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" },
  { value: "medium", label: "Medium", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400" },
  { value: "critical", label: "Critical", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
];

export default function BugReportButton() {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("bug");
  const [priority, setPriority] = useState("medium");

  const reset = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("bug");
    setPriority("medium");
    setSubmitted(false);
    setError("");
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    if (submitted) reset();
  }, [reset, submitting, submitted]);

  useEffect(() => { setOpen(false); reset(); }, [user?.id, user?.organization_id, reset]);

  const handleSubmit = useCallback(async () => {
    if (submitting || !title.trim() || !description.trim()) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/bug-reports", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          category,
          priority,
          page_url: window.location.origin + window.location.pathname,
          browser_info: navigator.userAgent,
        }),
      });
      if (!res.ok) throw new Error("Unable to submit feedback. Your text is still here; please try again.");
      setSubmitted(true);
    } catch (err) {
      setError("Unable to confirm submission. Your text is still here. Check your connection before retrying.");
    } finally {
      setSubmitting(false);
    }
  }, [title, description, category, priority, token, submitting]);

  const selectedCategory = CATEGORIES.find((c) => c.value === category)!;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 w-11 h-11 rounded-full bg-forest-600 hover:bg-forest-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        aria-label="Help and feedback" title="Report a bug or request a feature"
      >
        <Bug size={18} className="group-hover:scale-110 transition-transform" />
      </button>

      {/* Modal overlay */}
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
          <div className="relative bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto z-10">
            {/* Header */}
            <div className="sticky top-0 bg-card/95 backdrop-blur-md border-b border-border px-5 py-3.5 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-forest-500/10 flex items-center justify-center">
                  <Bug size={16} className="text-forest-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Submit Feedback</h3>
                  <p className="text-[10px] text-muted-foreground">Bug reports, features, questions</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={submitting} aria-label="Close feedback"
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {submitted ? (
              /* Success state */
              <div className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-forest-500/10 flex items-center justify-center mx-auto mb-4">
                  <Send size={22} className="text-forest-500" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-1.5">Submitted!</h4>
                <p className="text-sm text-muted-foreground mb-6">
                  Your feedback has been saved.
                </p>
                <button
                  onClick={handleClose}
                  className="px-5 py-2 text-sm font-medium bg-forest-600 hover:bg-forest-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              /* Form */
              <div className="p-5 space-y-4">
                {/* Category pills */}
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                    Type
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      const isSelected = category === cat.value;
                      return (
                        <button
                          key={cat.value} aria-pressed={isSelected}
                          onClick={() => setCategory(cat.value)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? "bg-forest-500/15 text-forest-600 dark:text-forest-400 ring-1 ring-forest-500/30"
                              : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                          }`}
                        >
                          <Icon size={13} className={isSelected ? cat.color : ""} />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="feedback-title" className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    Subject
                  </label>
                  <input
                    id="feedback-title" type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={
                      category === "bug"
                        ? "e.g. Inventory page doesn't load"
                        : category === "feature"
                        ? "e.g. Export reports as PDF"
                        : "Brief summary"
                    }
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/40 focus:border-forest-500 placeholder:text-muted-foreground/50"
                    autoFocus
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="feedback-description" className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                    Description
                  </label>
                  <textarea id="feedback-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={
                      category === "bug"
                        ? "What happened? What did you expect? Steps to reproduce..."
                        : "Describe what you need..."
                    }
                    rows={4}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500/40 focus:border-forest-500 placeholder:text-muted-foreground/50 resize-none"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                    Priority
                  </label>
                  <div className="flex gap-1.5">
                    {PRIORITIES.map((p) => (
                      <button
                        key={p.value} aria-pressed={priority === p.value}
                        onClick={() => setPriority(p.value)}
                        className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all text-center ${
                          priority === p.value
                            ? `${p.color} ring-1 ring-current/20`
                            : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Context info */}
                <div className="bg-secondary/30 rounded-lg px-3 py-2 text-[11px] text-muted-foreground space-y-0.5">
                  <p>Submitting as <span className="text-foreground font-medium">{user?.name || user?.email}</span></p>
                  <p className="truncate">Page: {window.location.pathname}</p>
                </div>

                {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
                <p className="text-xs text-muted-foreground">Closing keeps your text while you remain in the app. Avoid including passwords or private customer information.</p>
                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !title.trim() || !description.trim()}
                  className="w-full py-2.5 text-sm font-semibold bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      Submit {selectedCategory.label}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
